// AGC: useAuth - Authentication hook for components
// AGC: Provides easy access to auth state and permission checking

import { useCallback, useMemo } from 'react'
import { useAuthContext } from '../context/AuthContext'
import type { UserRole, UserProfile } from '../types/user'
import {
  canRoleManage,
  getAllowedRolesToCreate,
  canAccessAdmin as checkCanAccessAdmin,
  canManageUsers as checkCanManageUsers,
  canCreateSubManager as checkCanCreateSubManager,
  getRoleDisplayName,
  getRoleBadgeColor,
  ROLE_HIERARCHY,
} from '../types/user'

/**
 * AGC: Auth hook return type
 */
interface UseAuthReturn {
  // AGC: User data
  user: ReturnType<typeof useAuthContext>['user']
  profile: UserProfile | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  
  // AGC: Role info
  role: UserRole | null
  roleDisplayName: string
  roleBadgeColor: string
  roleLevel: number
  
  // AGC: Permission checks
  canAccessAdmin: boolean
  canManageUsers: boolean
  canCreateSubManager: boolean
  canCreateRole: (targetRole: UserRole) => boolean
  canManageRole: (targetRole: UserRole) => boolean
  allowedRolesToCreate: UserRole[]
  
  // AGC: Auth actions (from context)
  loginWithGoogle: () => Promise<void>
  loginWithApple: () => Promise<void>
  loginWithEmail: (credentials: { email: string; password: string }) => Promise<void>
  loginWithPhone: (phoneNumber: string, containerId: string) => Promise<void>
  verifyOTP: (otp: string) => Promise<void>
  register: (data: { email: string; password: string; displayName: string; phoneNumber?: string }) => Promise<void>
  logout: () => Promise<void>
  resetPassword: (email: string) => Promise<void>
  clearError: () => void
  
  // AGC: First login / biometric setup
  isFirstLogin: boolean
  setFirstLoginComplete: () => void
  
  // AGC: API helper
  getAuthToken: () => Promise<string | null>
  refreshProfile: () => Promise<void>
}

/**
 * AGC: useAuth hook - Main authentication hook for components
 * AGC: Combines AuthContext with permission helpers
 */
export const useAuth = (): UseAuthReturn => {
  const {
    user,
    isLoading,
    isAuthenticated,
    error,
    isFirstLogin,
    loginWithGoogle,
    loginWithApple,
    loginWithEmail,
    loginWithPhone,
    verifyOTP,
    register,
    logout,
    resetPassword,
    clearError,
    setFirstLoginComplete,
    getAuthToken,
    refreshProfile,
  } = useAuthContext()

  // AGC: Get user profile
  const profile = user?.profile || null
  
  // AGC: Get user role
  const role = profile?.role || null

  // AGC: Get role display name
  const roleDisplayName = useMemo(() => {
    return role ? getRoleDisplayName(role) : 'Não autenticado'
  }, [role])

  // AGC: Get role badge color
  const roleBadgeColor = useMemo(() => {
    return role ? getRoleBadgeColor(role) : 'bg-gray-400 text-white'
  }, [role])

  // AGC: Get role hierarchy level
  const roleLevel = useMemo(() => {
    return role ? ROLE_HIERARCHY[role] : 0
  }, [role])

  // AGC: Permission: can access admin panel
  const canAccessAdmin = useMemo(() => {
    return role ? checkCanAccessAdmin(role) : false
  }, [role])

  // AGC: Permission: can manage users
  const canManageUsers = useMemo(() => {
    return role ? checkCanManageUsers(role) : false
  }, [role])

  // AGC: Permission: can create sub-managers
  const canCreateSubManager = useMemo(() => {
    return role ? checkCanCreateSubManager(role) : false
  }, [role])

  // AGC: Get allowed roles that current user can create
  const allowedRolesToCreate = useMemo(() => {
    return role ? getAllowedRolesToCreate(role) : []
  }, [role])

  // AGC: Check if user can create a specific role
  const canCreateRole = useCallback((targetRole: UserRole): boolean => {
    if (!role) return false
    return getAllowedRolesToCreate(role).includes(targetRole)
  }, [role])

  // AGC: Check if user can manage a specific role
  const canManageRole = useCallback((targetRole: UserRole): boolean => {
    if (!role) return false
    return canRoleManage(role, targetRole)
  }, [role])

  return {
    // AGC: User data
    user,
    profile,
    isAuthenticated,
    isLoading,
    error,
    
    // AGC: Role info
    role,
    roleDisplayName,
    roleBadgeColor,
    roleLevel,
    
    // AGC: Permission checks
    canAccessAdmin,
    canManageUsers,
    canCreateSubManager,
    canCreateRole,
    canManageRole,
    allowedRolesToCreate,
    
    // AGC: Auth actions
    loginWithGoogle,
    loginWithApple,
    loginWithEmail,
    loginWithPhone,
    verifyOTP,
    register,
    logout,
    resetPassword,
    clearError,
    
    // AGC: First login / biometric
    isFirstLogin,
    setFirstLoginComplete,
    
    // AGC: API helper
    getAuthToken,
    refreshProfile,
  }
}

export default useAuth




